import { getEventDisplayLabel, getTrackerDisplayLabel } from "./displayLabels";
import type { SupportedLocale } from "@patient-watch/i18n";
import type { ExtendedEventViewT, StatefulDiaryT, StatefulTrackerViewT } from "@patient-watch/types";

import { isInjectionOrthopaedicUnset } from "./missingDetailsBuilders";
import { isProcedureSideUnset, normalizeProcedureSide } from "./procedureSide";

export interface ProcedureEventDisplay {
    eventId: string | null;
    heading: string;
    procedureDate: string | null;
    procedureDateRaw: string | null;
    sharedAttributes: ExtendedEventViewT["shared_attributes"] | StatefulDiaryT["shared_attributes"];
}

export interface ProcedureInformationDisplay {
    /** e.g. Surgery, Injection, Knee arthroscopy */
    heading: string | null;
    procedureDate: string | null;
    side: string | null;
    anatomicArea: string | null;
    events: ProcedureEventDisplay[];
}

export type ProcedureEventKind =
    | "injection"
    | "surgery"
    | "shockwave"
    | "product"
    | "consultation"
    | "medication"
    | "procedure";

export function getProcedureEventKind(event: ExtendedEventViewT): ProcedureEventKind {
    if ((event.injections_view?.length ?? 0) > 0) return "injection";
    if ((event.surgeries_view?.length ?? 0) > 0) return "surgery";
    if ((event.shockwaves_view?.length ?? 0) > 0) return "shockwave";
    if ((event.products_view?.length ?? 0) > 0) return "product";
    if ((event.educations_view?.length ?? 0) > 0) return "consultation";
    return "procedure";
}

export function eventHasProcedureContent(event: ExtendedEventViewT): boolean {
    return Boolean(
        event.injections_view?.length ||
        event.surgeries_view?.length ||
        event.shockwaves_view?.length ||
        event.products_view?.length ||
        event.educations_view?.length
    );
}

function getProcedureHeadingForEvent(event: ExtendedEventViewT, locale: SupportedLocale): string {
    return getEventDisplayLabel(event, locale);
}

function referenceDateForEvent(event: ExtendedEventViewT, diary: StatefulDiaryT): string | null | undefined {
    return event.shared_attributes?.reference_date ?? diary.shared_attributes?.reference_date;
}

export function getProcedureEventsFromDiary(
    diary: StatefulDiaryT,
    locale: SupportedLocale = "en-GB"
): ProcedureEventDisplay[] {
    return (diary.events_view ?? []).filter(eventHasProcedureContent).map((event) => {
        const referenceDate = referenceDateForEvent(event, diary);
        return {
            eventId: event.event_id ?? null,
            heading: getProcedureHeadingForEvent(event, locale),
            procedureDate: referenceDate ?? null,
            procedureDateRaw: referenceDate ?? null,
            sharedAttributes: event.shared_attributes ?? diary.shared_attributes,
        };
    });
}

export function getProcedureHeadingFromDiary(diary: StatefulDiaryT): string | null {
    return getProcedureEventsFromDiary(diary)[0]?.heading ?? null;
}

export interface ReferenceDateContext {
    /** Event heading, e.g. "Injection", "ACL repair". Null when no event anchors the date (e.g. a registry). */
    heading: string | null;
    kind: ProcedureEventKind;
}

/**
 * Heading + event kind for reference-date copy. Pass `forTracker` to only use events linked to that
 * tracker, so a registry tracker alongside an injection tracker is not labelled as the injection.
 */
export function getReferenceDateContextFromDiary(
    diary: StatefulDiaryT,
    locale: SupportedLocale = "en-GB",
    forTracker?: StatefulTrackerViewT
): ReferenceDateContext {
    const tracker =
        forTracker ??
        (diary.trackers_view ?? []).find((candidate) => candidate.shared_attributes?.reference_date == null) ??
        diary.trackers_view?.[0];
    const linkedEventIds = new Set((tracker?.events ?? []).map((event) => event.event_id));
    const trackerSharedAttributeId = tracker?.shared_attributes?.shared_attribute_id;
    const linkedEvent = (diary.events_view ?? []).find(
        (candidate) =>
            eventHasProcedureContent(candidate) &&
            (linkedEventIds.has(candidate.event_id ?? "") ||
                (trackerSharedAttributeId != null &&
                    candidate.shared_attributes?.shared_attribute_id === trackerSharedAttributeId))
    );
    const event = forTracker ? linkedEvent : (linkedEvent ?? (diary.events_view ?? []).find(eventHasProcedureContent));
    if (!event) {
        if (tracker?.management_types?.includes("medication")) {
            return {
                heading: getTrackerDisplayLabel(tracker, "Medication", locale),
                kind: "medication",
            };
        }
        // OTIVIO FlowOx programmes seed trackers only (required_events: []). Treat as product
        // so reference-date setup uses Behandlungsbeginn / FlowOx™ product start copy.
        if (diary.diary_type === "OTIVIO_DE" || diary.diary_type === "OTIVIO_DK") {
            return { heading: "FlowOx™", kind: "product" };
        }
        return { heading: null, kind: "procedure" };
    }
    return {
        heading: getProcedureHeadingForEvent(event, locale),
        kind: getProcedureEventKind(event),
    };
}

export function getProcedureInformationFromDiary(diary: StatefulDiaryT): ProcedureInformationDisplay {
    const events = getProcedureEventsFromDiary(diary);
    let side: string | null = null;
    let anatomicArea: string | null = null;

    for (const event of diary.events_view ?? []) {
        for (const injection of event.injections_view ?? []) {
            for (const detail of injection.injection_details ?? []) {
                if (!side && !isProcedureSideUnset(detail.side)) {
                    side = normalizeProcedureSide(detail.side);
                }
                if (!anatomicArea && !isInjectionOrthopaedicUnset(detail.orthopaedic_area)) {
                    anatomicArea = detail.orthopaedic_area ?? null;
                }
            }
        }

        for (const shockwave of event.shockwaves_view ?? []) {
            for (const detail of shockwave.shockwave_details ?? []) {
                if (!side && !isProcedureSideUnset(detail.side)) {
                    side = normalizeProcedureSide(detail.side);
                }
                if (!anatomicArea && !isInjectionOrthopaedicUnset(detail.orthopaedic_area)) {
                    anatomicArea = detail.orthopaedic_area ?? null;
                }
            }
        }

        for (const surgery of event.surgeries_view ?? []) {
            if (!side && !isProcedureSideUnset(surgery.side)) {
                side = normalizeProcedureSide(surgery.side);
            }
        }
    }

    const firstEvent = events[0];

    return {
        heading: firstEvent?.heading ?? null,
        procedureDate: firstEvent?.procedureDate ?? null,
        side,
        anatomicArea,
        events,
    };
}
